import React, { useRef, useState } from 'react';
import { Bold, Heading } from 'lucide-react';
import { LinkifiedText } from './LinkifiedText.tsx';

// A description box with simple formatting that is stored as plain text:
//   **bold**   (Ctrl+B / the B button)       # Heading   (Ctrl+Alt+H / the H button)
// While typing the raw marks are visible; when the box is not focused the text is shown
// formatted (bold words, bigger bold headings). Nothing is stored as HTML, so there is
// nothing to sanitise and older plain descriptions look exactly as before.

interface DescriptionEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Called when the box loses focus (used to save) */
  onBlur?: () => void;
  placeholder?: string;
  rows?: number;
  className?: string;
}

const HEADING_RE = /^(#{1,3})\s+(.*)$/;

/** Splits a line into plain and **bold** parts */
const renderInline = (line: string, keyPrefix: string): React.ReactNode[] => {
  const parts = line.split(/(\*\*[^*\n]+\*\*)/g);
  return parts
    .filter((p) => p !== '')
    .map((part, i) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
        <strong key={`${keyPrefix}-${i}`} className="font-extrabold">
          {part.slice(2, -2)}
        </strong>
      ) : (
        <LinkifiedText key={`${keyPrefix}-${i}`} text={part} />
      )
    );
};

export const FormattedDescription: React.FC<{ text: string }> = ({ text }) => (
  <div className="whitespace-pre-wrap break-words">
    {text.split('\n').map((line, i) => {
      const heading = line.match(HEADING_RE);
      if (heading) {
        const level = heading[1].length;
        return (
          <div
            key={i}
            className={`font-extrabold text-surface-900 dark:text-white ${
              level === 1 ? 'text-base mt-1' : level === 2 ? 'text-sm mt-1' : 'text-xs mt-0.5'
            }`}
          >
            {renderInline(heading[2], `h${i}`)}
          </div>
        );
      }
      return <div key={i}>{line === '' ? ' ' : renderInline(line, `l${i}`)}</div>;
    })}
  </div>
);

export const DescriptionEditor: React.FC<DescriptionEditorProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  rows = 4,
  className = '',
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [editing, setEditing] = useState(false);

  // Puts the text in place and restores the selection once React has re-rendered
  const apply = (next: string, selStart: number, selEnd: number) => {
    onChange(next);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(selStart, selEnd);
    });
  };

  const toggleBold = () => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: start, selectionEnd: end } = el;
    const selected = value.slice(start, end);

    // Already wrapped just outside the selection: take the marks off
    if (value.slice(start - 2, start) === '**' && value.slice(end, end + 2) === '**') {
      apply(value.slice(0, start - 2) + selected + value.slice(end + 2), start - 2, end - 2);
      return;
    }
    // The selection itself includes the marks
    if (selected.length > 4 && selected.startsWith('**') && selected.endsWith('**')) {
      const inner = selected.slice(2, -2);
      apply(value.slice(0, start) + inner + value.slice(end), start, start + inner.length);
      return;
    }
    if (start === end) {
      // Nothing selected: drop a pair of marks and put the cursor between them
      apply(value.slice(0, start) + '****' + value.slice(end), start + 2, start + 2);
    } else {
      apply(value.slice(0, start) + `**${selected}**` + value.slice(end), start + 2, end + 2);
    }
  };

  // Turns the line(s) under the cursor into a heading, or back into normal text
  const toggleHeading = () => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: start, selectionEnd: end } = el;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const nextBreak = value.indexOf('\n', end);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const lines = value.slice(lineStart, lineEnd).split('\n');
    const allHeadings = lines.every((l) => HEADING_RE.test(l));
    const changed = lines.map((l) => (allHeadings ? l.replace(/^#{1,3}\s+/, '') : HEADING_RE.test(l) ? l : `# ${l}`)).join('\n');
    const delta = changed.length - (lineEnd - lineStart);
    apply(value.slice(0, lineStart) + changed + value.slice(lineEnd), start + (allHeadings ? delta : Math.min(2, delta)), end + delta);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      toggleBold();
    } else if (mod && e.altKey && e.key.toLowerCase() === 'h') {
      e.preventDefault();
      toggleHeading();
    }
  };

  const toolbarButton =
    'p-1 rounded-md text-surface-500 hover:text-surface-900 dark:hover:text-white hover:bg-surface-200/70 dark:hover:bg-surface-700 transition-colors';

  if (!editing && value.trim()) {
    return (
      <div
        role="textbox"
        tabIndex={0}
        aria-label="Description (click to edit)"
        onClick={() => setEditing(true)}
        onFocus={() => setEditing(true)}
        title="Click to edit"
        className={`${className} cursor-text text-surface-900 dark:text-surface-100`}
      >
        <FormattedDescription text={value} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-0.5">
          {/* mouse-down is cancelled so the box keeps its focus while a button is used */}
          <button type="button" className={toolbarButton} title="Bold (Ctrl+B)" aria-label="Bold" onMouseDown={(e) => e.preventDefault()} onClick={toggleBold}>
            <Bold className="w-3.5 h-3.5" />
          </button>
          <button type="button" className={toolbarButton} title="Heading (Ctrl+Alt+H)" aria-label="Heading" onMouseDown={(e) => e.preventDefault()} onClick={toggleHeading}>
            <Heading className="w-3.5 h-3.5" />
          </button>
        </div>
        <span className="text-[10px] text-surface-400">Ctrl+B bold · # heading</span>
      </div>
      <textarea
        ref={ref}
        autoFocus={editing}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setEditing(true)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          setEditing(false);
          onBlur?.();
        }}
        placeholder={placeholder}
        className={className}
      />
    </div>
  );
};
